/**
 * Shared status configuration for Content Backfill tables.
 *
 * Centralizes status-to-indicator and status-to-tooltip mappings so both
 * BackfillRunsTable and BackfillItemsTable use a single source of truth.
 */
import { StatusIndicatorProps } from '@amzn/awsui-components-react/polaris/status-indicator';

import { getString } from '../../common/ui-string-labels/ui-strings-utils';
import {
  BackfillApprovalStatus,
  BackfillItemStatus,
  BackfillRunStatus
} from '../../services/content-backfill-service';

type IndicatorType = StatusIndicatorProps.Type;

// ─── Item / Approval Status ──────────────────────────────────────────────────

const ITEM_STATUS_INDICATOR_MAP: Record<string, IndicatorType> = {
  [BackfillApprovalStatus.APPROVED]: 'success',
  [BackfillItemStatus.IMPORT_FAILED]: 'error',
  [BackfillApprovalStatus.DECLINED]: 'stopped',
  [BackfillApprovalStatus.PENDING_REVIEW]: 'warning',
  [BackfillItemStatus.EXISTING]: 'info',
  [BackfillItemStatus.IMPORTED]: 'info'
};

const ITEM_STATUS_TOOLTIP_MAP: Record<string, string> = {
  [BackfillItemStatus.IMPORTED]: 'contentBackfill.tooltip.imported',
  [BackfillItemStatus.IMPORT_FAILED]: 'contentBackfill.tooltip.importFailed',
  [BackfillItemStatus.EXISTING]: 'contentBackfill.tooltip.existing',
  [BackfillApprovalStatus.DECLINED]: 'contentBackfill.tooltip.declined',
  [BackfillApprovalStatus.APPROVED]: 'contentBackfill.tooltip.approved',
  [BackfillApprovalStatus.PENDING_REVIEW]: 'contentBackfill.tooltip.pendingReview'
};

export const getItemStatusIndicatorType = (status: string | null): IndicatorType => {
  if (!status) return 'info';
  return ITEM_STATUS_INDICATOR_MAP[status.toUpperCase()] ?? 'info';
};

export const getItemStatusTooltip = (status: string | null): string | null => {
  if (!status) return null;
  const key = ITEM_STATUS_TOOLTIP_MAP[status.toUpperCase()];
  return key ? getString(key) : null;
};

// ─── Run Status ──────────────────────────────────────────────────────────────

const RUN_STATUS_INDICATOR_MAP: Record<string, IndicatorType> = {
  [BackfillRunStatus.COMPLETED]: 'success',
  [BackfillRunStatus.FAILED]: 'error',
  [BackfillRunStatus.IN_PROGRESS]: 'in-progress',
  [BackfillRunStatus.RUNNING]: 'in-progress'
};

export const getRunStatusIndicatorType = (status: string | null): IndicatorType => {
  if (!status) return 'info';
  return RUN_STATUS_INDICATOR_MAP[status.toUpperCase()] ?? 'info';
};
